import { garconClient } from "@apis/axiosClients"
import { toast } from "sonner"

export const updateCaseDescription = async (params, callback) => {
  try {
    const response = await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${params.case_guid}/description`,
      params,
    )
    if (response) {
      toast.success(`${response.statusText}! Case description was updated`)
      callback(params.case_description)
    }
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const updateCaseTypeCategory = async (
  params,
  reloadCase,
) => {
  try {
    await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${params.case_guid}/category`,
      params,
    )
    toast.success("Case category was updated")
    reloadCase()
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const updateCaseAssignment = async (
  params,
  userObject,
  callback,
) => {
  const user_id = params.assignToSelf ? userObject.user_id : userObject.value
  try {
    await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${params.caseGuid}/assignment`,
      { user_id: user_id },
    )
    toast.success("Case assignment was updated!")
    const updatedCaseAssignment = params.assignToSelf
      ? userObject.email
      : userObject.label
    callback(updatedCaseAssignment)
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const updateCaseSalesAgents = async (
  caseAgents,
  caseGuid,
  callback,
) => {
  const agentIds = caseAgents.map((agent) => agent.value)
  try {
    const response = await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${caseGuid}/sales_agents`,
      { sales_agents: agentIds },
    )
    toast.success(`${response.statusText}! Sales agents were updated!`)
    callback(caseAgents)
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const updateCaseInteractionChannel = async (
  params,
  callback,
) => {
  try {
    const response = await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${params.case_guid}/case_interaction_channel_code`,
      params,
    )
    toast.success(`${response.statusText}! Interaction channel updated!`)
    callback({
      label: params.to_case_interaction_channel,
      value: params.to_case_interaction_channel,
    })
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const addCaseChannel = async (
  newCaseChannelCode,
  caseGuid,
  reloadCase,
) => {
  try {
    await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${caseGuid}/add_case_channel/${newCaseChannelCode.value}`,
    )
    toast.success("Case channels updated!")
    reloadCase()
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const removeCaseChannel = async (
  removeCaseChannelCode,
  caseGuid,
  reloadCase,
) => {
  try {
    await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${caseGuid}/remove_case_channel/${removeCaseChannelCode.value}`,
    )
    toast.success("Case channels updated!")
    reloadCase()
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const saveCaseNote = async (
  params,
  callback,
  reloadCase,
) => {
  try {
    const response = await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${params.case_guid}/note`,
      params,
    )
    if (response.status === 200) {
      toast.success("Case notes updated!")
      reloadCase()
      callback("")
    }
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const uploadAttachment = async (
  formData,
  caseGuid,
  newCase = false,
  reloadCase,
) => {
  try {
    await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${caseGuid}/attach`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        skipAuditInterceptor: true,
      },
    )
    toast.success("Case attachments updated!")
    if (!newCase) {
      reloadCase()
    }
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const deleteAttachment = async (
  fileName,
  caseGuid,
  reloadCase,
) => {
  try {
    const response = await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${caseGuid}/delete_attach/${fileName}`,
    )
    toast.success(`${response.statusText}! Case attachments updated!`)
    reloadCase()
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const transitionCase = async (
  caseGuid,
  params,
  reloadCase,
) => {
  try {
    await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${caseGuid}/transition`,
      params,
    )
    toast.success("Case status changed!")
    reloadCase()
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const updateCaseOrder = async (payload, reloadCase) => {
  try {
    await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${payload.case_guid}/case_order`,
      payload,
    )
    toast.success(`Associated order updated!`)
    reloadCase()
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const updateRequestedCloseDate = async (
  payload,
  reloadCase,
) => {
  try {
    await garconClient.post(
      `${import.meta.env.VITE_GARCON_API}/cases/${payload.case_guid}/edit_close_date`,
      payload,
    )
    toast.success(`Requested Close Date Updated!`)
    reloadCase()
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}

export const updateCaseAttribute = async (
  caseGuid,
  caseAttribute,
  reloadCase,
) => {
  try {
    const payload = {
      case_attribute_id: caseAttribute.case_attribute_id,
      attribute_value: caseAttribute.value,
    }
    const response = await garconClient.put(
      `${import.meta.env.VITE_GARCON_API}/cases/${caseGuid}/attributes`,
      payload,
    )
    if (response.status === 200) {
      toast.success("Case Attributes Updated!")
      reloadCase()
    }
  } catch (error) {
    toast.error(`Something went wrong: ${error}`)
    throw error
  }
}
